define((require) => {
    "use strict";

    var $                       = require("jquery"),
        _                       = require("underscore"),
        DashboardNavigationView = require("ui/dashboard/DashboardNavigationView"),
        EditorEvents            = require("shared/services/EditorEvents"),
        ImprovedView            = require("editor/zbone/ImprovedView"),
        {Logger}                = require("shared/util/Logger"),
        Marionette              = require("marionette"),
        Promise                 = require("shared/util/Promise"),
        Preconditions           = require("shared/util/Preconditions"),
        SlimSpinnerView         = require("ui/common/SlimSpinnerView"),
        template                = require("ui/dashboard/DashboardView.tpl");


    var logger = new Logger("DashboardView"),
        Region = Marionette.Region;

    /**
     *
     * @class DashboardView
     *
     */
    return ImprovedView.extend("DashboardView", {

        className: "lazy dashboard-view pt-page",

        template: template,

        templateHelpers: {
            headerContentUnescaped: "<h1 class='title'>Dashboard</h1>"
        },

        regions: {
            dashboardNavigationRegion: ".dashboard-navigation-region",
            progressIndicatorRegion: ".progress-indicator-region",
            defaultFooterRegion: ".default-footer"
        },

        ui: {
            dashboardSection: "section.dashboard"
        },

        transitions: {
            in: "pt-page-scaleUp",
            out: "pt-page-scaleDown"
        },

        _dashboardNavigationView: null,

        /**
         * @type {Map.<Tab, Region>}
         */
        _regionsMap: null,

        /**
         * @type {Marionette.Region}
         */
        _activeRegion: null,

        /**
         * Reference to the current tab.
         *
         * @type {DashboardTabConfiguration}
         */
        _tab: null,

        initialize: function() {
            this._regionsMap = new Map();
        },

        serializeData: function() {
            return {};
        },

        viewScope: function() {
            return {
                /**
                 * Exposes the element that are to be considered the scrolling element for the "lazy"
                 * collection view(s). In this case we're scrolling the entire dashboard page, and not
                 * only the collection view.
                 *
                 * @type {$}
                 */
                scrollTarget: this.$el
            };
        },

        onShow: function() {
            this._createRegions();

            this._dashboardNavigationView = new DashboardNavigationView({tabs: this.options.tabs});
            this.dashboardNavigationRegion.show(this._dashboardNavigationView);

            this._invalidateCounting();
        },

        showTab: function(tab) {
            var self = this;

            // Defer to ensure we're 'shown'...
            _.defer(() => {
                self._tab = tab;
                self._dashboardNavigationView.setActiveTab(tab);

                self._activeRegion = self._regionsMap.get(tab);

                // Hide all but current...
                self._regionsMap.forEach((mappedRegion) => {
                    if (mappedRegion !== self._activeRegion) {
                        mappedRegion.el.hide();

                        self._viewActive(mappedRegion, false);
                    }
                });

                // Show the rendered view (if any), unless we need to reload/fetch the view options...
                if (self._activeRegion.currentView && !tab.get("reloadOptions")) {
                    // Partial views can change regardless of the current view.
                    // Make sure partial views get updated if the view exist (i.e. no reload happening)
                    // if the view has an `updatePartialView` method (e.g. DetailedImageLibraryCollectionView#updatePartialViews)
                    if (self._activeRegion.currentView.updatePartialViews) {
                        self._activeRegion.currentView.updatePartialViews(self.options.partialViewOptions);
                    }

                    self._activeRegion.el.show();
                    self._viewActive(self._activeRegion, true);
                } else {
                    var promise = tab.fetchOptions(self.options);

                    self._showSpinner(promise);
                    self._dashboardNavigationView.disableUntil(promise);

                    promise.then((options) => {
                        // Sanity check - in theory one could switch tabs
                        // during fetching of tab data, so bail out if tabs have changed at this point.
                        if (tab !== self._tab) {
                            return;
                        }

                        var View = tab.get("view"),
                            // By default, all subviews inherit the options from the main dashboard view.
                            viewOptions = _.extend(options, self.options),
                            view = new View(viewOptions);

                        self._activeRegion.el.show();
                        self._activeRegion.show(view);

                        logger.info("Dashboard '" + tab.get("name") + "' tab loaded and rendered.");

                        Preconditions.checkState(_.isFunction(view.getCountableEventSource), "Dashboard sub view must expose a getCountableEventSource method.");

                        // Listen for relevant changes in the sub view's collection(s) in order to update the counting labels.
                        var countableEventSourceEventSource = view.getCountableEventSource(),
                            sources = _.isArray(countableEventSourceEventSource) ? countableEventSourceEventSource : [countableEventSourceEventSource];

                        sources.forEach((eventSource) => {
                            self.stopListening(eventSource);
                            self.listenTo(eventSource, "add remove destroy change sync:state", _.debounce(self._invalidateCounting.bind(self), 300));
                        });
                    });
                }
            });

            var eventProperties = {
                dashboardType: tab.get("name"),
                triggerOrigin: "DashboardTab",
                projectId: self._project && self._project.get("id")
            };

            EditorEvents.trigger(EditorEvents.constants.OPEN_DASHBOARD, eventProperties);
        },

        /**
         * @private
         *
         * Invokes the 'active' method (if any) on the current view of the specified region.
         *
         * @param  {Region} region  The region holding the view.
         * @param  {Boolean} active The active state.
         */
        _viewActive: function(region, active) {
            if (region.currentView && _.isFunction(region.currentView.active)) {
                region.currentView.active(active);
            }
        },

        /**
         * Updates the counting label of the various tabs.
         *
         * @return {Promise} A promise that resolves when the counting has been fetched.
         */
        _invalidateCounting: function() {
            var promises = this.options.tabs.map((tab) => {
                return tab.invalidateLength();
            });

            return Promise.all(promises);
        },

        _showSpinner: function(promise) {
            this.progressIndicatorRegion.show(new SlimSpinnerView());
            this.progressIndicatorRegion.$el.show();

            promise.done(() => {
                if (this.progressIndicatorRegion.$el) {
                    this.progressIndicatorRegion.$el.hide();
                }
                this.progressIndicatorRegion.close();
            });
        },

        _createRegions: function() {
            this.options.tabs.each((tab) => {
                // Create the region element...
                var $element = $("<section />")
                        .hide() // Initially hidden...
                        .addClass(tab.get("slug") + "-region")
                        // This will enable fading when navigating between tabs...
                        .addClass("fade-in")
                        .insertAfter(this.ui.dashboardSection),
                    // Initialize the actual region...
                    region = new Region({el: $element});

                // We need to set this explicitly when creating regions "by hand" (to support viewScope)...
                region.parentView = this;

                this._regionsMap.set(tab, region);
            });
        },

        /**
         * Invoked when the router navigates from *this* view - checks whether the current view has any
         * unsaved changes. (Currently only the publish view uses this feature).
         *
         * @return {Boolean} <tt>true</tt> if unsaved changes, <tt>false</tt> otherwise.
         */
        beforeNavigate: function() {
            var view = this._activeRegion.currentView;

            // The view might have been disposed by then, check that it's not null before.
            if (view && _.isFunction(view.hasUnsavedChanges)) {
                return view.hasUnsavedChanges();
            }

            return false;
        },

        onClose: function() {
            this._regionsMap.forEach((region) => {
                region.close();
            });

            this._regionsMap.clear();
            this._regionsMap = null;
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/dashboard/DashboardView.js